import { invariant } from "@olenbetong/appframe-core";

export type LoginOptions = {
	remember?: boolean;
};

/**
 * Thrown by `login` when the site turns the credentials down, as opposed to not answering or
 * answering with an error of its own. Check `error.name === "LoginRejectedError"` where the
 * class itself may come from another copy of the package.
 */
export class LoginRejectedError extends Error {
	override name = "LoginRejectedError";
	/** The server's error code, e.g. 18456 for a wrong username or password. */
	errorCode?: number;

	constructor(message: string, errorCode?: number) {
		super(message);
		this.errorCode = errorCode;
	}
}

/** SQL Server's "Login failed for user": the username or password is wrong. */
export const SQL_LOGIN_FAILED = 18456;

/**
 * The error for a failed login. A `LoginRejectedError` only when the credentials themselves were
 * turned down - a 401, or the server's `{ error }` with SQL error 18456 - since callers delete a
 * saved password on a rejection. Anything else (a locked or expired account, a proxy's 403, a
 * rate limit, an answer without a code) is a plain `Error` carrying `status` / `errorCode`.
 */
export function createLoginError(message: string, details: { status?: number; errorCode?: number } = {}) {
	if (details.status === 401 || details.errorCode === SQL_LOGIN_FAILED) {
		return new LoginRejectedError(message, details.errorCode);
	}
	return Object.assign(new Error(message), details);
}

export interface IClient {
	readonly hostname: string;

	/**
	 * Wraps fetch and adds JSON headers (Accept and Content-Type) and X-Requested-With
	 * which is required by some handlers. If timeout is set in `init`, the request will
	 * be aborted if not finished by the amount of milliseconds set.
	 *
	 * If Content-Type should not be JSON, either pass in another Content-Type, or set
	 * isJson to false in `init`
	 */
	afFetch(url: string, init: RequestInit & { timeout?: number; isJson?: boolean }): Promise<Response>;
	fetch(input: any, init?: any): Promise<Response>;
	login(username: string, password: string, options?: LoginOptions): Promise<boolean>;
	setHostname(hostname?: string): void;
}

let defaultClient: IClient;

export function getDefaultClient() {
	invariant(defaultClient, "No default client has been set");
	return defaultClient;
}

export function setDefaultClient(client: IClient) {
	defaultClient = client;
}
